import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { AdminGuard, AdminTitle } from "@/components/admin/AdminGuard";
import { errText, getLookbook } from "@/lib/api";
import { adminAddLook, adminDeleteLook, adminProducts, adminReorderLooks, adminSaveLook, adminUpload } from "@/lib/admin";

const plain = (l) => ({ src: l.src, alt: l.alt, caption: l.caption, tags: l.tags.map(({ product_slug, x, y }) => ({ product_slug, x, y })) });

const LookRow = ({ l, i, n, products, setLooks, move }) => {
  const [pick, setPick] = useState(products[0]?.slug || "");
  const [caption, setCaption] = useState(l.caption);
  const img = useRef(null);
  const save = async (body) => { try { setLooks(await adminSaveLook(l.id, body)); toast.success("Saved"); } catch (e) { toast.error(errText(e)); } };
  const tag = (e) => {
    const r = img.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 10;
    const y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 10;
    save({ ...plain(l), tags: [...plain(l).tags, { product_slug: pick, x, y }] });
  };
  return (
    <li className="grid gap-6 border-b border-ink/10 py-6 md:grid-cols-[260px_1fr]" data-testid={`admin-look-${i}`}>
      <div className="relative cursor-crosshair overflow-hidden rounded-[16px]" onClick={tag} data-testid={`admin-look-image-${i}`}>
        <img ref={img} src={l.src} alt={l.alt} className="w-full" />
        {l.tags.map((t, k) => <span key={k} style={{ left: `${t.x}%`, top: `${t.y}%` }} className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink bg-lime" />)}
      </div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== l.caption && save({ ...plain(l), caption })} className="field !w-48" data-testid={`admin-look-caption-${i}`} />
          <button disabled={!i} onClick={() => move(i, -1)} aria-label="Move up" className="p-1 disabled:opacity-30" data-testid={`admin-look-up-${i}`}><ArrowUp size={16} /></button>
          <button disabled={i === n - 1} onClick={() => move(i, 1)} aria-label="Move down" className="p-1 disabled:opacity-30" data-testid={`admin-look-down-${i}`}><ArrowDown size={16} /></button>
          <button onClick={async () => window.confirm("Delete this look?") && setLooks(await adminDeleteLook(l.id))} aria-label="Delete" className="p-1 text-alert" data-testid={`admin-look-delete-${i}`}><Trash2 size={16} /></button>
        </div>
        <label className="block"><span className="label text-ink/60">Tag product (then click the photo where it is)</span>
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="field" data-testid={`admin-look-product-${i}`}>{products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}</select></label>
        <ul className="space-y-1 text-sm">{l.tags.map((t, k) => (
          <li key={k} className="flex items-center gap-3"><span className="font-mono text-xs">{t.x}% / {t.y}%</span>{t.product.name}
            <button onClick={() => save({ ...plain(l), tags: plain(l).tags.filter((_, j) => j !== k) })} className="link-u !text-[10px] text-alert" data-testid={`admin-look-untag-${i}-${k}`}>Remove</button></li>
        ))}</ul>
      </div>
    </li>
  );
};

const Panel = () => {
  const [looks, setLooks] = useState(null);
  const [products, setProducts] = useState([]);
  useEffect(() => { getLookbook().then(setLooks); adminProducts().then(setProducts).catch(() => {}); }, []);
  const upload = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    try { const r = await adminUpload(f); setLooks(await adminAddLook({ src: r.src, alt: "the suspects lookbook photo", caption: "New look", tags: [] })); toast.success("Photo added"); } catch (err) { toast.error(errText(err)); }
  };
  const move = async (i, d) => {
    const ids = looks.map((l) => l.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    setLooks(await adminReorderLooks(ids));
  };
  return (
    <>
      <AdminTitle right={<label className="btn btn-ink cursor-pointer" data-testid="admin-look-upload"><Upload size={15} /> Upload photo<input type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} className="sr-only" data-testid="admin-look-upload-input" /></label>}>Lookbook</AdminTitle>
      <ul className="mt-6">{looks?.map((l, i) => <LookRow key={l.id + l.tags.length + l.caption} l={l} i={i} n={looks.length} products={products} setLooks={setLooks} move={move} />)}</ul>
    </>
  );
};

export default function AdminLookbook() {
  return <AdminGuard><Panel /></AdminGuard>;
}
